/**
 * SkyeBasicThemes JavaScript class
 * This class can be used to implement basic themes on the Skye Learning website.
 * 
 * One of the major parameters that must be provided to this class is the themeJSON -
 * file which holds the necessary data for detecting, and activating themes.
 * 
 * A desired theme is to be set in the body element class attribute. Here are two
 * examples:
 * <body class="theme--skye-standard">
 * <body class="theme--skye-snow-mountain">
 * 
 * The basis of these themes is in the CSS styling that's contianed in the SCSS modules
 * for each theme. JavaScript supports some theme necessities that can't be achieved 
 * through styling. The themeJSON data will contain information for themes along with the 
 * classes for elements that are targeted and inspected for various reasons.
 * 
 * Here is an example of the themeJSON that should be provided to the class:
 * 
 * let themesData = {
  data: [
    {
      class: "theme--skye-standard",
      imgPrefix: "https://cdn.skyelearning.com/static/"
    },
    {
      class: "theme--skye-snow-mountain",
      imgPrefix:
        "https://cdn.skyelearning.com/static/themes/skye-snow-mountain/"
    }
  ],
  imgNames: {
    tablet: "skye-tab-logo.svg",
    desktop: "skye-desk-logo.svg"
  },
  imgTargets: {
    desktopLogo: ".auxiliary-navbar__desktop__logo",
    tabletLogo: ".auxiliary-navbar__tablet__logo"
  }
};
 * 
 * Since rollovers are important for themes the detectTheme() method can be used to give information
 * to the ImageRolloversClass, so that it can process rollovers and preloading for each theme.
 * 
 */

class SkyeBasicThemes {
  /**
   *
   * @param {object} bodyElement - HTML object of the body element. Should be query-selected when
   * provided as a variable.
   *
   * @param {object} themeJSON - JSON object that contains the data for themes (paths, image names,
   * theme classes, classes, etc.).
   *
   */
  constructor(bodyElement, themeJSON) {
    this.bodyElement = bodyElement;
    this.themeJSON = themeJSON.theme;
  }

  /**
   * Detects the desired theme that is indicated in the class attribute of the <body> tag.
   * This same method can be used to provide the ImageRolloverClass information about how -
   * to handle the image rollovers for themes, since the paths are going to be different -
   * for each theme.
   *
   * @returns object containing theme name and image path on the Skye CDN.
   *
   */
  detectTheme() {
    let detectedTheme = "";
    let themeClassArray = this.themeJSON.data;
    let bodyClassList = this.bodyElement.classList;
    let themeMsg = "";
    let themeImagePrefix = "";
    let themePresent = false;
  
    /* Search and compare values in <body> element's classList with that of the themeJSON.data[index].class data to find matches */
    for (var x = 0; x < themeClassArray.length; x++) {
      if (bodyClassList.contains(themeClassArray[x].class)) {
          detectedTheme = themeClassArray[x].class;
          themeImagePrefix = themeClassArray[x].imgPrefix;
          themeMsg = "The selected theme is " + detectedTheme;
          themePresent = true;
          break;
      } else {
          continue;
      }
    }
    
    if (themePresent === false) { 
      // Fail-: if no theme class is specified in the <body> tag will default to theme--skye-standard.
      let bodyEle = document.querySelector('body');
      bodyEle.classList.add('theme--skye-standard');
      detectedTheme = themeClassArray[0].class;
      themeImagePrefix = themeClassArray[0].imgPrefix;
      themeMsg = "The selected theme is theme--skye-standard";
    }
    
    return { imgPrefix: themeImagePrefix, msg: themeMsg, theme: detectedTheme };
  }

  /**
   * Activates the supporting theme JavaScript using data from the detectTheme method.
   *
   * @returns object - returns an object containing the desktop logo path, tablet logo path, and activation message.
   */
  activateTheme() {
    let themeName = this.detectTheme(); // Detect the theme.
    let bannerDesktopLogoPath = "";
    let bannerTabletLogoPath = "";
    let activationMsg = "";

    switch (themeName.theme) {
      case "theme--skye-standard": // Settings for standard theme.
        bannerDesktopLogoPath =
          this.themeJSON.data[0].imgPrefix + this.themeJSON.imgNames.desktop;
        bannerTabletLogoPath =
          this.themeJSON.data[0].imgPrefix + this.themeJSON.imgNames.tablet;
        //this.setTheme(bannerDesktopLogoPath, bannerTabletLogoPath);
        activationMsg = "Skye Standard Theme Activated";
        break;

      case "theme--skye-snow-mountain": // Settings for snow Mountain theme.
        bannerDesktopLogoPath =
          this.themeJSON.data[1].imgPrefix + this.themeJSON.imgNames.desktop;
        bannerTabletLogoPath =
          this.themeJSON.data[1].imgPrefix + this.themeJSON.imgNames.tablet;
        //this.setTheme(bannerDesktopLogoPath, bannerTabletLogoPath);
        activationMsg = "Skye Snow Mountain Theme Activated";
        break;

      case "default": // Default -> Settings for standard theme.
        bannerDesktopLogoPath =
          this.themeJSON.data[0].imgPrefix + this.themeJSON.imgNames.desktop;
        bannerTabletLogoPath =
          this.themeJSON.data[0].imgPrefix + this.themeJSON.imgNames.tablet;
        this.setTheme(bannerDesktopLogoPath, bannerTabletLogoPath);
        activationMsg = "Defaulted to Skye Standard Activation";
        break;

      default: // Default -> Settings for standard theme.
        bannerDesktopLogoPath =
          this.themeJSON.data[0].imgPrefix + this.themeJSON.imgNames.desktop;
        bannerTabletLogoPath =
          this.themeJSON.data[0].imgPrefix + this.themeJSON.imgNames.tablet;
        //this.setTheme(bannerDesktopLogoPath, bannerTabletLogoPath);
        activationMsg = "Skye Standard Activation";
    }
    return { desktopLogoPath: bannerDesktopLogoPath, tabletLogoPath: bannerTabletLogoPath, activeMsg: activationMsg };
  }

  /**
   * Assigns the desktopLogo element to a variable via the DOM.
   * Data to target elements is received from themeJSON.imgTargets.
   *
   * @return desktopLogo - desktopLogo img element as an object.
   */
  getDesktopLogo() {
    const desktopLogo = document.querySelector(
      this.themeJSON.imgTargets.desktopLogo
    );
    return desktopLogo;
  }

  /**
   * Assigns the desktopLogo element to a variable via the DOM.
   * Data to target element is received from themeJSON.imgTargets.
   *
   *  @return tabletLogo - tabletLogo img element as an object.
   */
  getTabletLogo() {
    const tabletLogo = document.querySelector(
      this.themeJSON.imgTargets.tabletLogo
    );
    return tabletLogo;
  }

  /**
   * Method sets the image sources for the desktop img elements that are targeted by the detected theme.
   * @param {string} desktopLogoPath - string for the desktop navigation logo
   * @param {string} tabletLogoPath - string for the tablet navigation logo
   * 
   * @returns object - returns an object that contains HTML objects for the desktop logo image, and tablet logo image elements.
   */
  setTheme(desktopLogoPath, tabletLogoPath) {
    let desktopLogo = this.getDesktopLogo();
    desktopLogo.src = desktopLogoPath;
    let tabletLogo = this.getTabletLogo();
    tabletLogo.src = tabletLogoPath;
    return { desktopLogoImg: desktopLogo, tabletLogoImg: tabletLogo };
  }

}

export default SkyeBasicThemes;
